import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Leaf, ArrowUpRight, Heart, ShoppingBag } from "lucide-react";
import userAPI from "../../api/UserApi";
import { showError } from "../../utils/toastUtils";
import { getDateAgo } from "../../helper/Helper";
import { containerVariants, itemVariants } from "./variants";
import wishlistAPI from "../../api/wishlistApi";

// Same fetch as sanwariyaLatest/src/pages/user/Dashboard.js:
// userAPI.fetchLatestOrder() -> res.code === 200 -> res.data
function Dashboard({ user }) {
  const [lastOrder, setLastOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [wishlist, setWishlist] = useState([]);
  const [loadingWishlist, setLoadingWishlist] = useState(true);

  useEffect(() => {
    fetchLatestOrder();
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const res = await wishlistAPI.listing();
      if (res.code === 200) {
        setWishlist(res.data?.products || []);
      }
    } catch (err) {
      // silently ignore, e.g. user not logged in
    } finally {
      setLoadingWishlist(false);
    }
  };

  const fetchLatestOrder = async () => {
    try {
      const res = await userAPI.fetchLatestOrder();
      if (res.code === 200) {
        setLastOrder(res.data);
      }
    } catch (err) {
      showError(err.message);
    } finally {
      setLoadingOrder(false);
    }
  };

  const firstItem = lastOrder?.items?.[0];
  const isDelivered = (lastOrder?.status || "").toLowerCase() === "delivered";

  return (
    <motion.div
      key="overview"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, y: -10 }}
      className="flex flex-col gap-3.5"
    >
      {/* Welcome */}
      <motion.div
        variants={itemVariants}
        className="acc-card relative overflow-hidden p-6 px-[26px] bg-[var(--color-accent-cream)]"
      >
        <div className="relative z-[2]">
          <span className="block mb-1.5 font-subheading text-[0.54rem] font-bold uppercase tracking-[0.2em] opacity-80 text-[var(--color-primary)]">
            Welcome Back
          </span>
          <h1 className="mb-2 font-heading text-[clamp(1.5rem,2.8vw,2.2rem)] leading-none tracking-[-0.02em] text-[var(--color-text-primary)]">
            Good Morning,{" "}
            <span className="font-accent text-[1.12em] text-[var(--color-primary)]">
              {user?.firstName || "there"}
            </span>
          </h1>
          {/* <p className="mb-3.5 max-w-[44ch] font-heading text-[0.9rem] italic font-light leading-[1.65] text-[var(--color-text-secondary)]">
            Your Lakadong Haldi subscription renews in 3 days. Your spice rack is almost at its finest.
          </p> */}
          {/* <button className="inline-flex items-center gap-1.5 border-0 border-b-[1.5px] border-b-[var(--color-primary)] bg-transparent pb-0.5 font-subheading text-[0.6rem] font-bold uppercase tracking-[0.15em] cursor-pointer text-[var(--color-text-primary)]">
            Browse New Arrivals <ArrowUpRight size={11} />
          </button> */}
        </div>
        <Flame
          size={160}
          className="pointer-events-none absolute top-1/2 -right-4 -translate-y-1/2 opacity-[0.08] text-[var(--color-accent-gold)]"
        />
      </motion.div>

      {/* 2-col */}
      <div className="acc-two-col grid grid-cols-2 gap-3.5">

        {/* Last order — real data from userAPI.fetchLatestOrder() */}
        <motion.div variants={itemVariants} className="acc-card p-[18px]">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-subheading text-[0.52rem] font-bold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              Last Order
            </span>
            {lastOrder && (
              <span
                className={`acc-badge ${isDelivered
                    ? "bg-[var(--color-secondary-lighter)] text-[var(--color-secondary-dark)]"
                    : "bg-[rgba(176,141,87,0.14)] text-[var(--color-accent-gold)]"
                  }`}
              >
                {lastOrder.status?.toUpperCase()}
              </span>
            )}
          </div>

          {loadingOrder ? (
            <p className="font-heading text-[0.85rem] text-[var(--color-text-muted)]">Loading…</p>
          ) : lastOrder ? (
            <>
              <div className="mb-3.5 flex items-center gap-3">
                <div className="flex h-[46px] w-[46px] flex-shrink-0 items-center justify-center rounded border border-[var(--card-border)] bg-[var(--bg-secondary)]">
                  <Leaf size={18} className="text-[var(--color-secondary)]" />
                </div>
                <div>
                  <p className="mb-0.5 font-heading text-[0.95rem] font-semibold leading-[1.2] text-[var(--color-text-primary)]">
                    {firstItem?.productName || `Order #${lastOrder.orderId}`}
                  </p>
                  <p className="font-subheading text-[0.52rem] tracking-[0.1em] text-[var(--color-text-muted)]">
                    {firstItem?.variantName ? `${firstItem.variantName} · ` : ""}{getDateAgo(lastOrder.orderDate || lastOrder.createdAt)}
                  </p>
                </div>
              </div>
              <button className="acc-btn-p w-full text-center">Reorder</button>
            </>
          ) : (
            <p className="font-heading text-[0.85rem] text-[var(--color-text-muted)]">
              No orders yet — go place your first one!
            </p>
          )}
        </motion.div>

        {/* Wishlist — real data from wishlistAPI.listing() */}
        <motion.div variants={itemVariants} className="acc-card p-[18px]">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-subheading text-[0.52rem] font-bold uppercase tracking-[0.18em] text-[var(--color-text-muted)]">
              Wishlist
            </span>
            <Heart size={14} className="text-[var(--color-primary)]" />
          </div>
          {loadingWishlist ? (
            <p className="font-heading text-[0.85rem] text-[var(--color-text-muted)]">Loading…</p>
          ) : wishlist.length > 0 ? (
            wishlist.slice(0, 3).map((product, i) => (
              <Link
                key={product._id || i}
                to={`/products/${product.slug}`}
                className={`flex items-center justify-between py-2 no-underline ${i < Math.min(wishlist.length, 3) - 1 ? "border-b border-[var(--card-border)]" : "border-b-0"
                  }`}
              >
                <span className="font-heading text-[0.88rem] text-[var(--color-text-primary)]">{product.title}</span>
                <ShoppingBag size={12} className="cursor-pointer text-[var(--color-text-muted)]" />
              </Link>
            ))
          ) : (
            <p className="font-heading text-[0.85rem] text-[var(--color-text-muted)]">
              No products in your wishlist yet.
            </p>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
}

export default Dashboard;